import React, { useEffect, useState, useContext, useRef } from "react";

import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import OnlyForSuperUser from "../OnlyForSuperUser";
import useAuth from "../../hooks/useAuth.js/index.js";

import { IconButton, InputAdornment, Chip, Button } from "@material-ui/core";

import { Colorize, AttachFile, Delete, Palette, Image, Language, Apps, PhoneAndroid, Share, Code, Brush } from "@material-ui/icons";
import ColorPicker from "../ColorPicker";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

import defaultLogoLight from "../../assets/logo.png";
import defaultLogoDark from "../../assets/logo-black.png";
import defaultLogoFavicon from "../../assets/favicon.ico";
import ColorBoxModal from "../ColorBoxModal/index.js";
import Checkbox from "@material-ui/core/Checkbox";
import { i18n } from "../../translate/i18n";
import SignupVerificationSettings from "./SignupVerificationSettings";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  sectionPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
    },
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  colorAdorment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid #ddd",
  },
  uploadInput: {
    display: "none",
  },
  uploadField: {
    "& .MuiInputBase-input": {
      cursor: "pointer",
    },
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  previewGrid: {
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  previewCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: "2px solid #e0e0e0",
    textAlign: "center",
    height: "120px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      height: "100px",
      padding: theme.spacing(1),
    },
  },
  previewCardLight: {
    backgroundColor: "#ffffff",
    borderColor: "#e0e0e0",
  },
  previewCardDark: {
    backgroundColor: "#424242",
    borderColor: "#666666",
  },
  previewCardFavicon: {
    backgroundColor: "#f5f5f5",
    borderColor: "#d0d0d0",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "80px",
    objectFit: "contain",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "60px",
    },
  },
  previewBackgroundImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: theme.spacing(0.5),
  },
  previewLabel: {
    fontSize: "0.75rem",
    color: "#666",
    marginTop: theme.spacing(0.5),
    fontWeight: 500,
  },
  previewLabelDark: {
    color: "#ccc",
  },
  languageSection: {
    marginTop: theme.spacing(1),
  },
  languageGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.5),
    },
  },
  languageItem: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    minWidth: "120px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "100px",
      padding: theme.spacing(0.5),
    },
  },
  languageCheckbox: {
    padding: theme.spacing(0.5),
  },
  languageLabel: {
    fontSize: "0.875rem",
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  colorSection: {
    [theme.breakpoints.down("sm")]: {
      "& .MuiGrid-item": {
        paddingBottom: theme.spacing(1),
      },
    },
  },
  logoSection: {
    [theme.breakpoints.down("sm")]: {
      "& .MuiGrid-item": {
        paddingBottom: theme.spacing(1),
      },
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1.5, 0),
    },
  },
  sectionActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  videoPositionLabel: {
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  videoPositionGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
    maxWidth: 140,
  },
  videoPositionCell: {
    width: 56,
    height: 56,
    border: "2px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.spacing(0.5),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  videoPositionCellSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + "20",
  },
}));

const LANGUAGE_OPTIONS = [
  { code: "pt-BR", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "ar", label: "عربي" },
];

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const [settingsLoaded, setSettingsLoaded] = useState({});

  const { getCurrentUserInfo } = useAuth();
  const [currentUser, setCurrentUser] = useState({});
  const [loading, setLoading] = useState(true);
  const { colorMode } = useContext(ColorModeContext);
  const [primaryColorLightModalOpen, setPrimaryColorLightModalOpen] =
    useState(false);
  const [primaryColorDarkModalOpen, setPrimaryColorDarkModalOpen] =
    useState(false);

  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const backgroundLightInput = useRef(null);
  const backgroundDarkInput = useRef(null);
  const icon192Input = useRef(null);
  const icon512Input = useRef(null);
  const appleTouchIconInput = useRef(null);
  const ogImageInput = useRef(null);
  const appNameInput = useRef(null);
  const [appName, setAppName] = useState(settingsLoaded.appName || "");
  const [appDescription, setAppDescription] = useState("");
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [customCss, setCustomCss] = useState("");
  const [poweredByText, setPoweredByText] = useState("");
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const [changelogUrl, setChangelogUrl] = useState("");
  const [savingPwa, setSavingPwa] = useState(false);
  const [savingOg, setSavingOg] = useState(false);
  const [savingCss, setSavingCss] = useState(false);

  const { update } = useSettings();

  function applyCustomCssToDocument(css) {
    const value = css != null ? String(css).trim() : "";
    let el = document.getElementById("whitelabel-custom-css");
    if (!value) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement("style");
      el.id = "whitelabel-custom-css";
      document.head.appendChild(el);
    }
    el.textContent = value;
  }

  function ogShareImageUrl(filename) {
    if (!filename) return "";
    const origin = (typeof window !== "undefined" && window.location.origin) || "";
    if (origin) return `${origin}/wl-public/og/${filename}`;
    return `${getBackendUrl()}/public/${filename}`;
  }

  function applyOgMetaToDocument({ title, description, imagePath }) {
    const ogTitleEl = document.getElementById("og-title");
    const ogDescEl = document.getElementById("og-description");
    const ogImageEl = document.getElementById("og-image");
    const metaDescEl = document.getElementById("meta-description");
    if (ogTitleEl && title) ogTitleEl.setAttribute("content", title);
    if (ogDescEl && description) ogDescEl.setAttribute("content", description);
    if (metaDescEl && description) metaDescEl.setAttribute("content", description);
    if (ogImageEl && imagePath) {
      ogImageEl.setAttribute("content", ogShareImageUrl(imagePath));
    }
  }

  function updateSettingsLoaded(key, value) {
    const persistKeys = [
      "primaryColorLight",
      "primaryColorDark",
      "appName",
      "appLogoLight",
      "appLogoDark",
      "appLogoFavicon",
      "appIcon192",
      "appIcon512",
      "appAppleTouchIcon"
    ];
    if (persistKeys.includes(key)) {
      try {
        localStorage.setItem(key, value != null ? value : "");
      } catch (e) {
        console.warn("localStorage.setItem failed for", key, e);
      }
    }
    const newSettings = { ...settingsLoaded };
    newSettings[key] = value;
    setSettingsLoaded(newSettings);
  }

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getCurrentUserInfo().then((u) => {
      const userData = u.user || u;
      if (isMounted) {
        setCurrentUser(userData);
      }
    });

    if (Array.isArray(settings) && settings.length) {
      const getVal = (key) => settings.find((s) => s.key === key)?.value;
      const primaryColorLight = getVal("primaryColorLight");
      const primaryColorDark = getVal("primaryColorDark");
      const appLogoLight = getVal("appLogoLight");
      const appLogoDark = getVal("appLogoDark");
      const appLogoFavicon = getVal("appLogoFavicon");
      const appLogoBackgroundLight = getVal("appLogoBackgroundLight");
      const appLogoBackgroundDark = getVal("appLogoBackgroundDark");
      const appLoginVideoYoutube = getVal("appLoginVideoYoutube");
      const appLoginVideoPosition = getVal("appLoginVideoPosition");
      const appName = getVal("appName");
      const changelogUrlSetting = getVal("changelogUrl");
      const enabledLanguagesSetting = getVal("enabledLanguages");

      // PWA & White Label avançado
      const appDescriptionVal = getVal("appDescription");
      const appIcon192 = getVal("appIcon192");
      const appIcon512 = getVal("appIcon512");
      const appAppleTouchIcon = getVal("appAppleTouchIcon");
      const ogTitleVal = getVal("ogTitle");
      const ogDescriptionVal = getVal("ogDescription");
      const ogImageVal = getVal("ogImage");
      const customCssVal = getVal("customCss");
      const poweredByTextVal = getVal("poweredByText");

      let langs = ["pt-BR", "en"];
      try {
        if (enabledLanguagesSetting) {
          langs = JSON.parse(enabledLanguagesSetting);
        }
      } catch { }

      if (isMounted) {
        setAppName(appName || "");
        setAppDescription(appDescriptionVal || "");
        setOgTitle(ogTitleVal || "");
        setOgDescription(ogDescriptionVal || "");
        setCustomCss(customCssVal || "");
        setPoweredByText(poweredByTextVal || "");
        setChangelogUrl(changelogUrlSetting || "https://docs.google.com/document/d/16DA0bMPFdxjpHLzXtLsxxm0oSiEzeA52-GGbNYLETvI/edit?usp=sharing");
        setEnabledLanguages(langs);
        setSettingsLoaded({
          ...settingsLoaded,
          primaryColorLight,
          primaryColorDark,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appLogoBackgroundLight,
          appLogoBackgroundDark,
          appLoginVideoYoutube: appLoginVideoYoutube || "",
          appLoginVideoPosition: appLoginVideoPosition || "bottom-right",
          appName,
          appDescription: appDescriptionVal,
          appIcon192,
          appIcon512,
          appAppleTouchIcon,
          ogTitle: ogTitleVal,
          ogDescription: ogDescriptionVal,
          ogImage: ogImageVal,
          customCss: customCssVal,
          poweredByText: poweredByTextVal,
          changelogUrl: changelogUrlSetting,
          enabledLanguages: langs,
        });
        setLoading(false);
      }
    }
    else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  async function persistSetting(key, value) {
    await update({ key, value });
    updateSettingsLoaded(key, value);
    if (key === "poweredByText" && colorMode?.setPoweredByText) {
      colorMode.setPoweredByText(value != null ? String(value).trim() : "");
    }
    if (key === "customCss") {
      applyCustomCssToDocument(value);
    }
  }

  async function handleSaveSetting(key, value, successMessage) {
    await persistSetting(key, value);
    toast.success(successMessage || "Configuração salva com sucesso.");
  }

  async function handleSavePwaSection() {
    setSavingPwa(true);
    try {
      await persistSetting("appDescription", appDescription);
      await persistSetting("poweredByText", poweredByText);
      toast.success("Configurações PWA e rodapé salvas.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar configurações PWA.");
    } finally {
      setSavingPwa(false);
    }
  }

  async function handleSaveOgSection() {
    setSavingOg(true);
    try {
      await persistSetting("ogTitle", ogTitle);
      await persistSetting("ogDescription", ogDescription);
      applyOgMetaToDocument({
        title: ogTitle || appName,
        description: ogDescription || appDescription,
        imagePath: settingsLoaded.ogImage,
      });
      toast.success("Configurações de compartilhamento social salvas.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar Open Graph.");
    } finally {
      setSavingOg(false);
    }
  }

  async function handleSaveCssSection() {
    setSavingCss(true);
    try {
      await persistSetting("customCss", customCss);
      toast.success("CSS personalizado aplicado.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar CSS personalizado.");
    } finally {
      setSavingCss(false);
    }
  }

  async function handleSaveEnabledLanguages(newLangs) {
    await handleSaveSetting("enabledLanguages", newLangs);
    setEnabledLanguages(newLangs);
  }

  const uploadLogo = async (e, mode) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    const formData = new FormData();

    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("file", file);

    await api
      .post("/settings-whitelabel/logo", formData, {
        onUploadProgress: (event) => {
          let progress = Math.round((event.loaded * 100) / event.total);
          console.log(`A imagem está ${progress}% carregada... `);
        },
      })
      .then((response) => {
        // Mapear mode para a chave correta no state
        const keyMap = {
          Light: "appLogoLight",
          Dark: "appLogoDark",
          Favicon: "appLogoFavicon",
          BackgroundLight: "appLogoBackgroundLight",
          BackgroundDark: "appLogoBackgroundDark",
          Icon192: "appIcon192",
          Icon512: "appIcon512",
          AppleTouchIcon: "appAppleTouchIcon",
          OgImage: "ogImage"
        };
        const stateKey = keyMap[mode] || `appLogo${mode}`;
        updateSettingsLoaded(stateKey, response.data);

        // Atualizar colorMode apenas para logos principais (Light, Dark, Favicon)
        const colorModeMap = { Light: true, Dark: true, Favicon: true };
        if (colorModeMap[mode] && colorMode[`setAppLogo${mode}`]) {
          colorMode[`setAppLogo${mode}`](
            getBackendUrl() + "/public/" + response.data
          );
        }
        if (mode === "OgImage") {
          applyOgMetaToDocument({
            title: ogTitle || appName,
            description: ogDescription || appDescription,
            imagePath: response.data,
          });
          toast.success("Imagem de preview salva.");
        } else if (["Icon192", "Icon512", "AppleTouchIcon"].includes(mode)) {
          toast.success("Ícone PWA salvo.");
        }
      })
      .catch((err) => {
        console.error(`Houve um problema ao realizar o upload da imagem.`);
        console.log(err);
      });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <div>Carregando configurações...</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <OnlyForSuperUser
        user={currentUser}
        yes={() => (
          <>
            {/* Seção de Configurações Gerais */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <Apps className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.sections.general")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t("whitelabel.sections.generalDescription")}
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3}>
                <Grid xs={12} sm={6} md={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="appname-field"
                      label={i18n.t("whitelabel.appName")}
                      variant="outlined"
                      name="appName"
                      value={appName}
                      inputRef={appNameInput}
                      onChange={(e) => {
                        setAppName(e.target.value);
                      }}
                      onBlur={async (_) => {
                        await handleSaveSetting("appName", appName);
                        colorMode.setAppName(appName || "Multi100");
                      }}
                      size="small"
                    />
                  </FormControl>
                </Grid>
                {/* Campo de Link do Drive de Updates - apenas para Super Admin */}
                {currentUser?.super && (
                  <Grid item xs={12} sm={6}>
                    <FormControl className={classes.formField} fullWidth>
                      <TextField
                        id="changelogUrl-field"
                        label="Link do Drive de Logs de Updates"
                        variant="outlined"
                        name="changelogUrl"
                        value={changelogUrl}
                        onChange={(e) => {
                          setChangelogUrl(e.target.value);
                        }}
                        onBlur={async (_) => {
                          await handleSaveSetting("changelogUrl", changelogUrl);
                        }}
                        size="small"
                        placeholder="https://docs.google.com/document/d/..."
                        helperText="Link do Google Docs com as atualizações do sistema"
                      />
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Seção de Cores */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <Palette className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.sections.colors")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t("whitelabel.sections.colorsDescription")}
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3} className={classes.colorSection}>
                <Grid xs={12} sm={6} md={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="primary-color-light-field"
                      label={i18n.t("whitelabel.primaryColorLight")}
                      variant="outlined"
                      value={settingsLoaded.primaryColorLight || ""}
                      onClick={() => setPrimaryColorLightModalOpen(true)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              style={{
                                backgroundColor: settingsLoaded.primaryColorLight,
                              }}
                              className={classes.colorAdorment}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => setPrimaryColorLightModalOpen(true)}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                    />
                  </FormControl>
                  <ColorBoxModal
                    open={primaryColorLightModalOpen}
                    handleClose={() => setPrimaryColorLightModalOpen(false)}
                    onChange={(color) => {
                      console.log("🔍 [Whitelabel] Color changed:", color);
                      handleSaveSetting("primaryColorLight", `#${color.hex}`);
                      colorMode.setPrimaryColorLight(`#${color.hex}`);
                    }}
                    currentColor={settingsLoaded.primaryColorLight}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="primary-color-dark-field"
                      label={i18n.t("whitelabel.primaryColorDark")}
                      variant="outlined"
                      value={settingsLoaded.primaryColorDark || ""}
                      onClick={() => setPrimaryColorDarkModalOpen(true)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              style={{
                                backgroundColor: settingsLoaded.primaryColorDark,
                              }}
                              className={classes.colorAdorment}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => setPrimaryColorDarkModalOpen(true)}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                    />
                  </FormControl>
                  <ColorBoxModal
                    open={primaryColorDarkModalOpen}
                    handleClose={() => setPrimaryColorDarkModalOpen(false)}
                    onChange={(color) => {
                      handleSaveSetting("primaryColorDark", `#${color.hex}`);
                      colorMode.setPrimaryColorDark(`#${color.hex}`);
                    }}
                    currentColor={settingsLoaded.primaryColorDark}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Seção de Logos e Imagens */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <Image className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.sections.logos")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t("whitelabel.sections.logosDescription")}
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3} className={classes.logoSection}>
                <Grid xs={12} sm={6} md={4} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="logo-light-upload-field"
                      label={i18n.t("whitelabel.logoLight")}
                      variant="outlined"
                      value={settingsLoaded.appLogoLight || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoLight && (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  handleSaveSetting("appLogoLight", "");
                                  colorMode.setAppLogoLight(defaultLogoLight);
                                }}
                              >
                                <Delete
                                  titleAccess={i18n.t("whitelabel.delete")}
                                />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-logo-light-button"
                              ref={logoLightInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "Light")}
                            />
                            <label htmlFor="upload-logo-light-button">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  logoLightInput.current.click();
                                }}
                              >
                                <AttachFile
                                  titleAccess={i18n.t("whitelabel.upload")}
                                />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="logo-dark-upload-field"
                      label={i18n.t("whitelabel.logoDark")}
                      variant="outlined"
                      value={settingsLoaded.appLogoDark || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoDark && (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  handleSaveSetting("appLogoDark", "");
                                  colorMode.setAppLogoDark(defaultLogoDark);
                                }}
                              >
                                <Delete
                                  titleAccess={i18n.t("whitelabel.delete")}
                                />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-logo-dark-button"
                              ref={logoDarkInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "Dark")}
                            />
                            <label htmlFor="upload-logo-dark-button">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  logoDarkInput.current.click();
                                }}
                              >
                                <AttachFile
                                  titleAccess={i18n.t("whitelabel.upload")}
                                />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="logo-favicon-upload-field"
                      label={i18n.t("whitelabel.favicon")}
                      variant="outlined"
                      value={settingsLoaded.appLogoFavicon || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoFavicon && (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  handleSaveSetting("appLogoFavicon", "");
                                  colorMode.setAppLogoFavicon(defaultLogoFavicon);
                                }}
                              >
                                <Delete
                                  titleAccess={i18n.t("whitelabel.delete")}
                                />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-logo-favicon-button"
                              ref={logoFaviconInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "Favicon")}
                            />
                            <label htmlFor="upload-logo-favicon-button">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  logoFaviconInput.current.click();
                                }}
                              >
                                <AttachFile
                                  titleAccess={i18n.t("whitelabel.upload")}
                                />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12} sm={6} md={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="background-light-upload-field"
                      label={i18n.t("whitelabel.backgroundLight")}
                      variant="outlined"
                      value={settingsLoaded.appLogoBackgroundLight || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoBackgroundLight && (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  handleSaveSetting("appLogoBackgroundLight", "");
                                }}
                              >
                                <Delete
                                  titleAccess={i18n.t("whitelabel.delete")}
                                />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-background-light-button"
                              ref={backgroundLightInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "BackgroundLight")}
                            />
                            <label htmlFor="upload-background-light-button">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  backgroundLightInput.current.click();
                                }}
                              >
                                <AttachFile
                                  titleAccess={i18n.t("whitelabel.upload")}
                                />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12} sm={6} md={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="background-dark-upload-field"
                      label={i18n.t("whitelabel.backgroundDark")}
                      variant="outlined"
                      value={settingsLoaded.appLogoBackgroundDark || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoBackgroundDark && (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  handleSaveSetting("appLogoBackgroundDark", "");
                                }}
                              >
                                <Delete
                                  titleAccess={i18n.t("whitelabel.delete")}
                                />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-background-dark-button"
                              ref={backgroundDarkInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "BackgroundDark")}
                            />
                            <label htmlFor="upload-background-dark-button">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                  backgroundDarkInput.current.click();
                                }}
                              >
                                <AttachFile
                                  titleAccess={i18n.t("whitelabel.upload")}
                                />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="login-video-youtube"
                      label={i18n.t("whitelabel.loginVideoYoutube")}
                      placeholder="https://www.youtube.com/watch?v=..."
                      variant="outlined"
                      value={settingsLoaded.appLoginVideoYoutube || ""}
                      size="small"
                      onChange={(e) => updateSettingsLoaded("appLoginVideoYoutube", e.target.value)}
                      onBlur={(e) => {
                        const v = (e.target.value || "").trim();
                        handleSaveSetting("appLoginVideoYoutube", v);
                      }}
                      helperText={i18n.t("whitelabel.loginVideoYoutubeHelper")}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12} sm={6} item>
                  <Typography className={classes.videoPositionLabel}>
                    {i18n.t("whitelabel.loginVideoPosition")}
                  </Typography>
                  <Box className={classes.videoPositionGrid}>
                    {["top-left", "top-right", "bottom-left", "bottom-right"].map((pos) => (
                      <Box
                        key={pos}
                        className={`${classes.videoPositionCell} ${
                          (settingsLoaded.appLoginVideoPosition || "bottom-right") === pos
                            ? classes.videoPositionCellSelected
                            : ""
                        }`}
                        onClick={() => {
                          updateSettingsLoaded("appLoginVideoPosition", pos);
                          handleSaveSetting("appLoginVideoPosition", pos);
                        }}
                        title={i18n.t(`whitelabel.videoPosition.${pos}`)}
                      >
                        <span style={{ fontSize: 10, color: "inherit" }}>
                          {pos === "top-left" && "↖"}
                          {pos === "top-right" && "↗"}
                          {pos === "bottom-left" && "↙"}
                          {pos === "bottom-right" && "↘"}
                        </span>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* Preview Section */}
              <div className={classes.previewContainer}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
                  {i18n.t("whitelabel.preview")}
                </Typography>
                <Grid container spacing={2} className={classes.previewGrid}>
                  <Grid xs={12} sm={6} md={3} item>
                    <div className={`${classes.previewCard} ${classes.previewCardLight}`}>
                      <img
                        className={classes.previewImage}
                        src={settingsLoaded.appLogoLight ?
                          getBackendUrl() + "/public/" + settingsLoaded.appLogoLight :
                          defaultLogoLight
                        }
                        alt={i18n.t("whitelabel.preview") + " light-logo"}
                        onError={(e) => {
                          e.target.src = defaultLogoLight;
                        }}
                      />
                      <div className={classes.previewLabel}>
                        {i18n.t("whitelabel.logoLight")}
                      </div>
                    </div>
                  </Grid>

                  <Grid xs={12} sm={6} md={3} item>
                    <div className={`${classes.previewCard} ${classes.previewCardDark}`}>
                      <img
                        className={classes.previewImage}
                        src={settingsLoaded.appLogoDark ?
                          getBackendUrl() + "/public/" + settingsLoaded.appLogoDark :
                          defaultLogoDark
                        }
                        alt={i18n.t("whitelabel.preview") + " dark-logo"}
                        onError={(e) => {
                          e.target.src = defaultLogoDark;
                        }}
                      />
                      <div className={`${classes.previewLabel} ${classes.previewLabelDark}`}>
                        {i18n.t("whitelabel.logoDark")}
                      </div>
                    </div>
                  </Grid>

                  <Grid xs={12} sm={6} md={3} item>
                    <div className={`${classes.previewCard} ${classes.previewCardFavicon}`}>
                      <img
                        className={classes.previewImage}
                        src={settingsLoaded.appLogoFavicon ?
                          getBackendUrl() + "/public/" + settingsLoaded.appLogoFavicon :
                          defaultLogoFavicon
                        }
                        alt={i18n.t("whitelabel.preview") + " favicon"}
                        onError={(e) => {
                          e.target.src = defaultLogoFavicon;
                        }}
                      />
                      <div className={classes.previewLabel}>
                        {i18n.t("whitelabel.favicon")}
                      </div>
                    </div>
                  </Grid>

                  <Grid xs={12} sm={6} md={6} item>
                    <div className={`${classes.previewCard} ${classes.previewCardLight}`}>
                      {settingsLoaded.appLogoBackgroundLight ? (
                        <img
                          className={classes.previewBackgroundImage}
                          src={
                            getBackendUrl() +
                            "/public/" +
                            settingsLoaded.appLogoBackgroundLight
                          }
                          alt={i18n.t("whitelabel.preview") + " background-light"}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className={classes.previewLabel}>
                          {i18n.t("whitelabel.backgroundLight")}
                        </div>
                      )}
                    </div>
                  </Grid>

                  <Grid xs={12} sm={6} md={6} item>
                    <div className={`${classes.previewCard} ${classes.previewCardDark}`}>
                      {settingsLoaded.appLogoBackgroundDark ? (
                        <img
                          className={classes.previewBackgroundImage}
                          src={
                            getBackendUrl() +
                            "/public/" +
                            settingsLoaded.appLogoBackgroundDark
                          }
                          alt={i18n.t("whitelabel.preview") + " background-dark"}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className={`${classes.previewLabel} ${classes.previewLabelDark}`}>
                          {i18n.t("whitelabel.backgroundDark")}
                        </div>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Paper>

            {/* Seção PWA (Progressive Web App) */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <PhoneAndroid className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    PWA (App Instalável)
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Configure como o app aparece quando instalado no celular ou desktop
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3}>
                <Grid xs={12} sm={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Nome na instalação do PWA"
                      variant="outlined"
                      value={appDescription}
                      onChange={(e) => setAppDescription(e.target.value)}
                      size="small"
                      placeholder="MultiFlow PRO"
                      helperText="Nome e ícone exibidos ao instalar o app no navegador/celular."
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Texto 'Powered by'"
                      variant="outlined"
                      value={poweredByText}
                      onChange={(e) => setPoweredByText(e.target.value)}
                      size="small"
                      placeholder="Powered by OtimizAPP"
                      helperText="Texto exibido no rodapé do sistema (login e painel)"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Box className={classes.sectionActions}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSavePwaSection}
                  disabled={savingPwa}
                >
                  {savingPwa ? "Salvando..." : "Salvar PWA e rodapé"}
                </Button>
              </Box>

              <Divider className={classes.divider} />
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                Ícones PWA
              </Typography>
              <Typography className={classes.sectionSubtitle} style={{ marginBottom: 16 }}>
                Ícones que aparecem na tela inicial do celular ao instalar o app. Recomendado: PNG quadrado com fundo transparente.
              </Typography>

              <Grid container spacing={3}>
                <Grid xs={12} sm={4} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Ícone 192x192 (PNG)"
                      variant="outlined"
                      value={settingsLoaded.appIcon192 || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appIcon192 && (
                              <IconButton size="small" onClick={() => handleSaveSetting("appIcon192", "")}>
                                <Delete titleAccess="Remover" />
                              </IconButton>
                            )}
                            <input type="file" accept="image/png" ref={icon192Input} className={classes.uploadInput} onChange={(e) => uploadLogo(e, "Icon192")} />
                            <IconButton size="small" onClick={() => icon192Input.current.click()}>
                              <AttachFile titleAccess="Upload" />
                            </IconButton>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Ícone 512x512 (PNG)"
                      variant="outlined"
                      value={settingsLoaded.appIcon512 || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appIcon512 && (
                              <IconButton size="small" onClick={() => handleSaveSetting("appIcon512", "")}>
                                <Delete titleAccess="Remover" />
                              </IconButton>
                            )}
                            <input type="file" accept="image/png" ref={icon512Input} className={classes.uploadInput} onChange={(e) => uploadLogo(e, "Icon512")} />
                            <IconButton size="small" onClick={() => icon512Input.current.click()}>
                              <AttachFile titleAccess="Upload" />
                            </IconButton>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={4} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Apple Touch Icon (PNG)"
                      variant="outlined"
                      value={settingsLoaded.appAppleTouchIcon || ""}
                      size="small"
                      className={classes.uploadField}
                      helperText="Ícone para dispositivos Apple (180x180 recomendado)"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appAppleTouchIcon && (
                              <IconButton size="small" onClick={() => handleSaveSetting("appAppleTouchIcon", "")}>
                                <Delete titleAccess="Remover" />
                              </IconButton>
                            )}
                            <input type="file" accept="image/png" ref={appleTouchIconInput} className={classes.uploadInput} onChange={(e) => uploadLogo(e, "AppleTouchIcon")} />
                            <IconButton size="small" onClick={() => appleTouchIconInput.current.click()}>
                              <AttachFile titleAccess="Upload" />
                            </IconButton>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* Preview dos ícones PWA */}
              <div className={classes.previewContainer}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
                  Preview dos Ícones
                </Typography>
                <Grid container spacing={2}>
                  {settingsLoaded.appIcon192 && (
                    <Grid xs={6} sm={3} item>
                      <div className={`${classes.previewCard} ${classes.previewCardFavicon}`}>
                        <img
                          className={classes.previewImage}
                          src={getBackendUrl() + "/public/" + settingsLoaded.appIcon192}
                          alt="Icon 192"
                          style={{ width: 48, height: 48 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <div className={classes.previewLabel}>192x192</div>
                      </div>
                    </Grid>
                  )}
                  {settingsLoaded.appIcon512 && (
                    <Grid xs={6} sm={3} item>
                      <div className={`${classes.previewCard} ${classes.previewCardFavicon}`}>
                        <img
                          className={classes.previewImage}
                          src={getBackendUrl() + "/public/" + settingsLoaded.appIcon512}
                          alt="Icon 512"
                          style={{ width: 64, height: 64 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <div className={classes.previewLabel}>512x512</div>
                      </div>
                    </Grid>
                  )}
                  {settingsLoaded.appAppleTouchIcon && (
                    <Grid xs={6} sm={3} item>
                      <div className={`${classes.previewCard} ${classes.previewCardFavicon}`}>
                        <img
                          className={classes.previewImage}
                          src={getBackendUrl() + "/public/" + settingsLoaded.appAppleTouchIcon}
                          alt="Apple Touch"
                          style={{ width: 48, height: 48, borderRadius: 10 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <div className={classes.previewLabel}>Apple Touch</div>
                      </div>
                    </Grid>
                  )}
                </Grid>
              </div>
            </Paper>

            {/* Seção Open Graph (Compartilhamento Social) */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <Share className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Compartilhamento Social (Open Graph)
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Configure como o link do sistema aparece quando compartilhado no WhatsApp, Facebook, LinkedIn, etc.
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3}>
                <Grid xs={12} sm={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Título (og:title)"
                      variant="outlined"
                      value={ogTitle}
                      onChange={(e) => setOgTitle(e.target.value)}
                      size="small"
                      placeholder="OtimizAPP - Atendimento Inteligente"
                      helperText="Título que aparece no preview do link compartilhado"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Descrição (og:description)"
                      variant="outlined"
                      value={ogDescription}
                      onChange={(e) => setOgDescription(e.target.value)}
                      size="small"
                      placeholder="Sistema completo de atendimento multicanal"
                      helperText="Descrição que aparece abaixo do título no preview"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="Imagem de Preview (og:image)"
                      variant="outlined"
                      value={settingsLoaded.ogImage || ""}
                      size="small"
                      className={classes.uploadField}
                      helperText="Imagem 1200x630 recomendada para preview em redes sociais"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.ogImage && (
                              <IconButton size="small" onClick={() => handleSaveSetting("ogImage", "")}>
                                <Delete titleAccess="Remover" />
                              </IconButton>
                            )}
                            <input type="file" accept="image/*" ref={ogImageInput} className={classes.uploadInput} onChange={(e) => uploadLogo(e, "OgImage")} />
                            <IconButton size="small" onClick={() => ogImageInput.current.click()}>
                              <AttachFile titleAccess="Upload" />
                            </IconButton>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Box className={classes.sectionActions}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveOgSection}
                  disabled={savingOg}
                >
                  {savingOg ? "Salvando..." : "Salvar compartilhamento social"}
                </Button>
              </Box>

              {/* Preview Open Graph */}
              {(ogTitle || ogDescription || settingsLoaded.ogImage) && (
                <div className={classes.previewContainer}>
                  <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
                    Preview do Compartilhamento
                  </Typography>
                  <div style={{
                    maxWidth: 500,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    overflow: "hidden",
                    backgroundColor: "#f0f2f5",
                  }}>
                    {settingsLoaded.ogImage && (
                      <img
                        src={ogShareImageUrl(settingsLoaded.ogImage)}
                        alt="OG Preview"
                        style={{ width: "100%", height: 200, objectFit: "cover" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div style={{ padding: "12px 16px" }}>
                      <div style={{ fontSize: 12, color: "#65676b", marginBottom: 4, textTransform: "uppercase" }}>
                        {window.location.hostname}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#1c1e21", marginBottom: 4 }}>
                        {ogTitle || appName || "Título do App"}
                      </div>
                      <div style={{ fontSize: 14, color: "#65676b" }}>
                        {ogDescription || appDescription || "Descrição do App"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Paper>

            {/* Seção Verificação de Cadastro */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <PhoneAndroid className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Verificação de Cadastro
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Configure SMTP e API WhatsApp para enviar o código de confirmação no registro de novas empresas.
                  </Typography>
                </div>
              </div>
              <SignupVerificationSettings />
            </Paper>

            {/* Seção CSS Personalizado */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <Code className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    CSS Personalizado
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Insira CSS customizado para ajustar detalhes visuais do sistema. Cuidado: CSS incorreto pode quebrar a interface.
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3}>
                <Grid xs={12} item>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      label="CSS Personalizado"
                      variant="outlined"
                      value={customCss}
                      onChange={(e) => setCustomCss(e.target.value)}
                      size="small"
                      multiline
                      minRows={4}
                      maxRows={12}
                      placeholder={`.MuiDrawer-paper { border-radius: 0 12px 12px 0; }\n/* Altere elementos visuais aqui */`}
                      helperText="CSS aplicado globalmente após clicar em Salvar."
                      InputProps={{
                        style: { fontFamily: "'Roboto Mono', monospace", fontSize: 13 },
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Box className={classes.sectionActions}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveCssSection}
                  disabled={savingCss}
                >
                  {savingCss ? "Salvando..." : "Salvar CSS personalizado"}
                </Button>
              </Box>
            </Paper>

            {/* Seção de Idiomas */}
            <Paper className={classes.sectionPaper}>
              <div className={classes.sectionHeader}>
                <Language className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.sections.languages")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t("whitelabel.sections.languagesDescription")}
                  </Typography>
                </div>
              </div>

              <div className={classes.languageSection}>
                <div className={classes.languageGrid}>
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <div key={lang.code} className={classes.languageItem}>
                      <Checkbox
                        className={classes.languageCheckbox}
                        checked={enabledLanguages.includes(lang.code)}
                        onChange={(e) => {
                          let newLangs = e.target.checked
                            ? [...enabledLanguages, lang.code]
                            : enabledLanguages.filter((c) => c !== lang.code);
                          if (newLangs.length === 0) {
                            toast.error(
                              i18n.t("whitelabel.atLeastOneLanguage")
                            );
                            return;
                          }
                          handleSaveEnabledLanguages(newLangs);
                        }}
                        color="primary"
                        size="small"
                      />
                      <span className={classes.languageLabel}>
                        {lang.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Paper>
          </>
        )}
      />
    </div>
  );
}